import React, { useEffect, useState, useRef } from 'react';
import { X, CheckCircle2, AlertTriangle, AlertCircle, Info, Loader2 } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading' | 'default';

export interface ActiveToast {
  id: string;
  message: string;
  type: ToastType;
  durationMs?: number;
  createdAt: number;
  details?: any;
  paused?: boolean;
}

type ToastListener = (toasts: ActiveToast[]) => void;
const listeners = new Set<ToastListener>();
let activeToasts: ActiveToast[] = [];

function emit() {
  listeners.forEach(l => l([...activeToasts]));
}

export const customToast = {
  show(message: string, type: ToastType = 'default', options?: { id?: string; duration?: number; details?: any }) {
    const id = options?.id || Math.random().toString(36).substring(2);
    const existingIndex = activeToasts.findIndex(t => t.id === id);

    const newToast: ActiveToast = {
      id,
      message,
      type,
      durationMs: options?.duration ?? (type === 'loading' ? undefined : 5000),
      createdAt: Date.now(),
      details: options?.details,
    };

    if (existingIndex > -1) {
      activeToasts[existingIndex] = newToast;
    } else {
      activeToasts.push(newToast);
    }
    emit();

    // Auto-remove se não for loading
    if (type !== 'loading') {
      const timeoutDuration = options?.duration ?? 5000;
      const checkAndDismiss = () => {
        const t = activeToasts.find(toast => toast.id === id && toast.createdAt === newToast.createdAt);
        if (t) {
          if (t.paused) {
            setTimeout(checkAndDismiss, 1000);
          } else {
            activeToasts = activeToasts.filter(toast => toast.id !== id);
            emit();
          }
        }
      };
      setTimeout(checkAndDismiss, timeoutDuration);
    }
    return id;
  },

  success(message: string, options?: { id?: string; duration?: number }) {
    return this.show(message, 'success', options);
  },
  error(message: string | Error | any, options?: { id?: string; duration?: number; details?: any } | any) {
    let msg = typeof message === 'string' ? message : (message?.response?.data?.message || message?.response?.data?.error?.message || message?.message || 'Comunicação incompleta com o servidor.');
    let details = options?.details;

    // Se o 1º argumento for o próprio objeto de erro (ex: axios error / Error)
    if (message instanceof Error || (message && typeof message === 'object' && ('response' in message || 'message' in message))) {
      details = message;
      msg = message?.response?.data?.message || message?.response?.data?.error?.message || message?.message || msg;
    }

    // Se o 2º argumento for o erro passado diretamente (ex: toast.error('Falha ao salvar', err))
    if (!details && options && (options instanceof Error || typeof options === 'object')) {
      if ('response' in options || 'message' in options || 'status' in options || 'stack' in options) {
        details = options;
      }
    }

    // Tratamento elegante de mensagens genéricas
    if (typeof msg === 'string') {
      const lower = msg.toLowerCase().trim();
      if (lower === 'erro inesperado' || lower === 'internal server error' || lower === 'erro interno do servidor') {
        msg = 'Comunicação com o servidor temporariamente indisponível. Tente novamente.';
      } else if (lower.startsWith('erro ao salvar') || lower.startsWith('erro ao criar') || lower.startsWith('erro ao atualizar')) {
        msg = 'Não foi possível concluir o salvamento. Verifique os dados e tente novamente.';
      }
    }

    // Se nenhum detalhe foi fornecido, gera o diagnóstico de contexto da sessão
    if (!details) {
      details = {
        mensagem: msg,
        timestamp: new Date().toISOString(),
        url: typeof window !== 'undefined' ? window.location.href : '',
      };
    }

    return this.show(msg, 'error', {
      id: typeof options?.id === 'string' ? options.id : undefined,
      duration: typeof options?.duration === 'number' ? options.duration : undefined,
      details,
    });
  },
  warning(message: string, options?: { id?: string; duration?: number }) {
    return this.show(message, 'warning', options);
  },
  info(message: string, options?: { id?: string; duration?: number }) {
    return this.show(message, 'info', options);
  },
  loading(message: string, options?: { id?: string }) {
    return this.show(message, 'loading', options);
  },
  dismiss(id?: string) {
    if (id) {
      activeToasts = activeToasts.filter(t => t.id !== id);
    } else {
      activeToasts = [];
    }
    emit();
  },
  pause(id: string) {
    const t = activeToasts.find(toast => toast.id === id);
    if (t) {
      t.paused = true;
      emit();
    }
  },
  promise<T>(
    promise: Promise<T> | (() => Promise<T>),
    options: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((err: any) => string);
    }
  ) {
    const id = this.loading(options.loading);
    const actualPromise = typeof promise === 'function' ? promise() : promise;
    actualPromise
      .then((data) => {
        const successMsg = typeof options.success === 'function' ? options.success(data) : options.success;
        this.success(successMsg, { id });
      })
      .catch((err) => {
        const errorMsg = typeof options.error === 'function' ? options.error(err) : options.error;
        this.error(errorMsg, { id, details: err });
      });
    return actualPromise;
  }
};

export const toast = Object.assign(
  (message: string, options?: { id?: string }) => customToast.show(message, 'default', options),
  {
    success: (msg: string, opts?: any) => customToast.success(msg, opts),
    error: (msg: string, opts?: any) => customToast.error(msg, opts),
    warning: (msg: string, opts?: any) => customToast.warning(msg, opts),
    info: (msg: string, opts?: any) => customToast.info(msg, opts),
    /** Alias sonner — vários telas usam toast.message */
    message: (msg: string, opts?: any) => customToast.info(msg, opts),
    loading: (msg: string, opts?: any) => customToast.loading(msg, opts),
    dismiss: (id?: string) => customToast.dismiss(id),
    promise: <T extends unknown>(p: any, opts: any) => customToast.promise<T>(p, opts)
  }
);

function CountdownDialog({ toast: t }: { toast: ActiveToast }) {
  const durationSeconds = t.durationMs ? Math.round(t.durationMs / 1000) : 5;
  const [timeLeft, setTimeLeft] = useState(durationSeconds);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPaused, setIsPaused] = useState(!!t.paused);
  const [copyText, setCopyText] = useState('Copiar');
  const typeRef = useRef(t.type);

  // Sync pause state from global toast state
  useEffect(() => {
    if (t.paused) {
      setIsPaused(true);
    }
  }, [t.paused]);

  // Reset do timer se o tipo mudar de loading para outro
  useEffect(() => {
    if (t.type !== 'loading') {
      setTimeLeft(durationSeconds);
    }
    typeRef.current = t.type;
  }, [t.type, durationSeconds]);

  // Efeito do contador regressivo
  useEffect(() => {
    if (t.type === 'loading' || isPaused) return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [t.type, isPaused]);

  const handleClose = () => {
    customToast.dismiss(t.id);
  };

  const handlePause = () => {
    setIsPaused(true);
    customToast.pause(t.id);
  };

  const formatDetails = (details: any, fallbackMessage?: string): string => {
    if (!details) {
      return JSON.stringify({
        mensagem: fallbackMessage || 'Erro na operação',
        timestamp: new Date().toISOString(),
        url: typeof window !== 'undefined' ? window.location.href : '',
      }, null, 2);
    }
    if (details instanceof Error) {
      const err = details as any;
      if (err.response?.data) {
        return typeof err.response.data === 'object'
          ? JSON.stringify(err.response.data, null, 2)
          : String(err.response.data);
      }
      return JSON.stringify({
        erro: err.name || 'Error',
        mensagem: err.message,
        codigo: err.code,
        stack: err.stack,
      }, null, 2);
    }
    if (typeof details === 'object') {
      try {
        if (details.response?.data) {
          return typeof details.response.data === 'object'
            ? JSON.stringify(details.response.data, null, 2)
            : String(details.response.data);
        }
        return JSON.stringify(details, null, 2);
      } catch {
        return String(details);
      }
    }
    return String(details);
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    handlePause();
    const text = formatDetails(t.details, t.message);
    navigator.clipboard.writeText(text).then(() => {
      setCopyText('Copiado!');
      setTimeout(() => setCopyText('Copiar'), 2000);
    }).catch(err => {
      console.error('Failed to copy text: ', err);
    });
  };

  const getIcon = () => {
    switch (t.type) {
      case 'success':
        return <CheckCircle2 size={24} color="#10b981" />;
      case 'error':
      case 'warning':
        return <AlertTriangle size={24} color="#f59e0b" />;
      case 'info':
        return <Info size={24} color="#3b82f6" />;
      case 'loading':
        return <Loader2 size={24} color="#6366f1" className="animate-spin" />;
      default:
        return <AlertTriangle size={24} color="#f59e0b" />;
    }
  };

  const getTitle = () => {
    switch (t.type) {
      case 'success': return 'Sucesso';
      case 'error':
      case 'warning':
        return 'Atenção';
      case 'info': return 'Informação';
      case 'loading': return 'Processando';
      default: return 'Atenção';
    }
  };

  const detailsText = formatDetails(t.details, t.message);

  return (
    <div 
      style={{
        background: 'var(--card-override, white)',
        borderRadius: 16,
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.08)',
        border: '1px solid var(--border-color, #e2e8f0)',
        overflow: 'hidden',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        animation: 'ac-modal-slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Corpo do Dialog */}
      <div style={{ display: 'flex', gap: 14, padding: '20px 24px 16px 20px', alignItems: 'flex-start' }}>
        <div style={{ flexShrink: 0 }}>{getIcon()}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h4 style={{ margin: '0 0 4px', fontSize: '0.92rem', fontWeight: 700, color: 'var(--text-main, #0f172a)' }}>
            {getTitle()}
          </h4>
          <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-muted, #4b5563)', lineHeight: 1.5 }}>
            {t.message}
          </p>

          {(t.details || t.type === 'error') && (
            <div 
              style={{ 
                marginTop: 10, 
                borderTop: '1px dashed #e2e8f0', 
                paddingTop: 8,
                width: '100%'
              }}
              onClick={handlePause}
            >
              <div 
                style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  cursor: 'pointer' 
                }}
                onClick={() => {
                  setIsExpanded(!isExpanded);
                  handlePause();
                }}
              >
                <span style={{ fontSize: '0.74rem', fontWeight: 500, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4, userSelect: 'none' }}>
                  <span style={{ fontSize: '0.65rem' }}>{isExpanded ? '▼' : '▶'}</span>
                  <span>Mais detalhes</span>
                </span>
                {isExpanded && (
                  <button
                    onClick={handleCopy}
                    style={{
                      border: '1px solid #e2e8f0',
                      background: 'white',
                      color: '#64748b',
                      borderRadius: 4,
                      padding: '2px 8px',
                      fontSize: '0.68rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {copyText}
                  </button>
                )}
              </div>
              {isExpanded && (
                <pre 
                  style={{ 
                    margin: '8px 0 0 0', 
                    padding: 10,
                    background: '#f8fafc', 
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    fontSize: '0.72rem', 
                    color: '#475569', 
                    maxHeight: 150, 
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    fontFamily: 'monospace'
                  }}
                >
                  {detailsText}
                </pre>
              )}
            </div>
          )}
        </div>
        {t.type !== 'loading' && (
          <button 
            onClick={handleClose}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: 4,
              borderRadius: 6,
              color: '#94a3b8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Rodapé do Dialog */}
      <div 
        style={{ 
          padding: '10px 20px 12px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderTop: '1px solid var(--border-color, #f1f5f9)',
          background: '#f8fafc',
          fontSize: '0.72rem',
          color: '#94a3b8'
        }}
      >
        <span>{t.type === 'loading' ? 'Por favor, aguarde...' : isPaused ? 'Tempo pausado' : `Fecha em ${timeLeft}s`}</span>
        {t.type !== 'loading' && (
          <button
            onClick={handleClose}
            style={{
              border: 'none',
              background: 'var(--primary-override, #0f172a)',
              color: 'white',
              borderRadius: 8,
              padding: '6px 14px',
              fontSize: '0.72rem',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
            }}
          >
            Fechar
          </button>
        )}
      </div>

      {/* Barra de progresso do timer regressivo */}
      {t.type !== 'loading' && (
        <div style={{ height: 3, width: '100%', background: '#e2e8f0' }}>
          <div 
            key={t.type + '-' + t.createdAt} // força re-render/re-animar ao mudar tipo
            style={{
              height: '100%',
              background: t.type === 'success' ? '#10b981' : t.type === 'error' || t.type === 'warning' ? '#f59e0b' : '#3b82f6',
              animation: `ac-toast-timer ${durationSeconds}s linear forwards`,
              animationPlayState: isPaused ? 'paused' : 'running',
              transformOrigin: 'left',
            }}
          />
        </div>
      )}

      <style>{`
        @keyframes ac-modal-slide-up {
          from { transform: scale(0.96) translateY(8px); opacity: 0; }
          to { transform: scale(1) translateY(0); opacity: 1; }
        }
        @keyframes ac-toast-timer {
          from { transform: scaleX(1); }
          to { transform: scaleX(0); }
        }
      `}</style>
    </div>
  );
}

export function Toaster() {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);

  useEffect(() => {
    const handleUpdate = (updated: ActiveToast[]) => {
      setToasts(updated);
    };
    listeners.add(handleUpdate);
    handleUpdate([...activeToasts]);
    return () => {
      listeners.delete(handleUpdate);
    };
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.45)', // escurece o fundo
        backdropFilter: 'blur(4px)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
      onClick={() => {
        // Permite fechar apenas os toasts que não forem do tipo loading
        const dismissibleToasts = toasts.filter(t => t.type !== 'loading');
        dismissibleToasts.forEach(t => customToast.dismiss(t.id));
      }}
    >
      <div 
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          maxWidth: 420,
          width: '100%',
        }}
      >
        {toasts.map((t) => (
          <CountdownDialog key={t.id} toast={t} />
        ))}
      </div>
    </div>
  );
}
